import { Outlet, createFileRoute } from '@tanstack/react-router'

import { FiredAlertHost } from '@/components/layout/FiredAlertHost'
import EmptyState from '@/components/ui/EmptyState'
import { ToastHost } from '@/components/ui/Toast'
import { usePushRegistration } from '@/features/push/lib/usePushRegistration'
import { useAuthStore } from '@/lib/auth/store'
import { isEmbedded } from '@/lib/embed/bridge'
import { cn } from '@/lib/utils'
import { appBeforeLoad } from '@/routes/layouts/app.guard'

/**
 * A signed-in page that owns the whole viewport: no top nav, no rail, no
 * bottom bar. Mounted by `layout('workspace', ...)` in routes/chart.ts, so it
 * adds no URL segment.
 *
 * The same gate as the `app` layout (`appBeforeLoad`), since the pages in it
 * read the member's own data. What it leaves behind is AppShell's chrome, and
 * of AppShell's other mounts only the toast host is kept: a failed bars read
 * or a refused layout save raises a toast, and without a host it would be
 * silent. The fired-alert card stays too: a member charting a stock is who
 * most wants their alert to fire on screen. The Mitra panel and the
 * market-rail prefetch stay with the shell; nothing on a chart reads them.
 *
 * `--header-h` is set to the height of TradingView's own header toolbar, the
 * strip with save, settings and fullscreen at its right end, plus the chart tab
 * strip above it (outside the Flutter app): the fired-alert
 * card is offset by that token, and with no bar of ours it must clear the
 * library's. `EmptyState size="page"` (ChartNotFound) reads it too.
 *
 * `h-dvh`, not `min-h-dvh`: the page is a fixed column and the chart inside
 * it fills all of it (the chart has no bar of its own), so a taller child
 * must not push the page into a scroll of its own. `overflow-hidden` is what holds that on the iOS
 * dynamic toolbar.
 */
function WorkspaceLayout() {
  usePushRegistration()
  const ended = useAuthStore((state) => state.status === 'anonymous') && isEmbedded()
  return (
    <div
      className={cn(
        'flex h-dvh flex-col overflow-hidden bg-background text-foreground',
        // The chart tab strip (40px from `lg`) over the library's header, except in the Flutter
        // app, which draws no strip.
        isEmbedded() ? '[--header-h:--spacing(10)]' : '[--header-h:--spacing(20)]',
      )}
    >
      {ended ? (
        <SessionEnded />
      ) : (
        <>
          <Outlet />
          <FiredAlertHost />
        </>
      )}
      <ToastHost />
    </div>
  )
}

/**
 * Inside the Flutter app, a session that is gone (or never came) ends here,
 * in place of the chart: the app was told (lib/embed/start.ts) and signs the
 * member in itself. No action: there is no sign-in on this side to offer.
 */
function SessionEnded() {
  return (
    <main
      aria-label="Session ended"
      className="flex min-h-0 flex-1 items-center justify-center p-4"
    >
      <EmptyState
        size="page"
        headingLevel="h1"
        title="Your session ended"
        description="Sign in again in the app."
      />
    </main>
  )
}

/**
 * In a browser tab, the signed-in gate (`appBeforeLoad`, to /signin). Inside
 * the Flutter app there is no /signin to send anyone to, so the guard only
 * waits for the app's answer and lets the layout draw `SessionEnded` when
 * there is no session.
 */
const workspaceBeforeLoad = async (context: { location: { href: string } }): Promise<void> => {
  if (!isEmbedded()) return appBeforeLoad(context)
  await useAuthStore.getState().ensureSession()
}

export const Route = createFileRoute('/_workspace')({
  beforeLoad: workspaceBeforeLoad,
  component: WorkspaceLayout,
})
